import PropTypes from 'prop-types';
import { Star, MapPin, Clock } from 'lucide-react';
import { useVenuesStore } from '@/features/venues/store/useVenuesStore';
import { Stack, Text, Heading } from '@/components/ui/index.js';
import styles from './VenueCard.module.css';

/**
 * VenueCard - Card displaying venue information
 * 
 * Shows venue image, name, sports, rating, distance, amenities,
 * and open/closed status. Used in both list and map views.
 */
export function VenueCard({ venue, variant = 'default', onClick, isSelected }) {
  const { getSportIcon, getAmenityIcon } = useVenuesStore();

  const cardClasses = [
    styles.card,
    styles[variant],
    isSelected ? styles.selected : '',
    onClick ? styles.clickable : '',
  ].filter(Boolean).join(' ');

  return (
    <article 
      className={cardClasses}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Image Section */}
      <div className={styles.imageContainer}>
        <div className={styles.imagePlaceholder}>
          <span className={styles.placeholderIcon}>🏟️</span>
        </div>
        <div className={`${styles.statusBadge} ${venue.isOpen ? styles.open : styles.closed}`}>
          {venue.isOpen ? 'Open' : 'Closed'}
        </div>
      </div>

      {/* Content Section */}
      <div className={styles.content}>
        <Stack direction="column" gap={2}>
          {/* Name & Rating */}
          <div className={styles.headerRow}>
            <Heading level={4} size="sm" color="primary" className={styles.name}>
              {venue.name}
            </Heading>
            <div className={styles.rating}>
              <Star size={14} className={styles.starIcon} fill="currentColor" />
              <Text size="sm" weight="semibold" color="primary">{venue.rating}</Text>
              <Text size="xs" color="muted">({venue.reviewCount})</Text>
            </div>
          </div>

          {/* Sports */}
          <div className={styles.sportsRow}>
            {venue.sports.slice(0, 3).map(sport => (
              <span key={sport} className={styles.sportBadge}>
                <span className={styles.sportIcon}>{getSportIcon(sport)}</span>
                <Text size="xs" color="primary">{sport}</Text>
              </span>
            ))}
            {venue.sports.length > 3 && (
              <Text size="xs" color="muted" className={styles.moreSports}>
                +{venue.sports.length - 3}
              </Text>
            )}
          </div>

          {/* Distance & Hours */}
          <div className={styles.metaRow}>
            <div className={styles.metaItem}>
              <MapPin size={14} className={styles.metaIcon} />
              <Text size="xs" color="secondary">{venue.distance} mi</Text>
            </div>
            <div className={styles.metaItem}>
              <Clock size={14} className={styles.metaIcon} />
              <Text size="xs" color="secondary">{venue.hours}</Text>
            </div>
          </div>

          {/* Amenities */}
          <div className={styles.amenitiesRow}>
            {venue.amenities.slice(0, 4).map(amenity => (
              <div key={amenity} className={styles.amenity} title={amenity}>
                <span className={styles.amenityIcon}>{getAmenityIcon(amenity)}</span>
              </div>
            ))}
          </div>

          {/* Action Button */}
          <button className={styles.viewButton}>
            View Details
          </button>
        </Stack>
      </div>
    </article>
  );
}

VenueCard.propTypes = {
  venue: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    sports: PropTypes.arrayOf(PropTypes.string).isRequired,
    rating: PropTypes.number.isRequired,
    reviewCount: PropTypes.number.isRequired,
    distance: PropTypes.number.isRequired,
    address: PropTypes.string.isRequired,
    amenities: PropTypes.arrayOf(PropTypes.string).isRequired,
    isOpen: PropTypes.bool.isRequired,
    hours: PropTypes.string.isRequired,
    coordinates: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  variant: PropTypes.oneOf(['default', 'compact', 'preview']),
  onClick: PropTypes.func,
  isSelected: PropTypes.bool,
};

export default VenueCard;
